import { useState, useEffect, useCallback } from "react";
import { DatasetService } from "../services/DatasetService";
import { ApiKeyOrGetter } from "../services/GeneralService";

// Tag configuration type from backend
export interface TagConfig {
  label: string;
  description: string;
  color: string;
}

// Display configuration with Tailwind classes
export interface TagDisplayConfig {
  label: string;
  description: string;
  className: string;
}

// Color to Tailwind class mapping
const COLOR_CLASS_MAP: Record<string, string> = {
  cyan: "border-cyan-500/30 text-cyan-700 bg-cyan-50 dark:bg-cyan-950 dark:text-cyan-300",
  amber: "border-amber-500/30 text-amber-700 bg-amber-50 dark:bg-amber-950 dark:text-amber-300",
  green: "border-green-500/30 text-green-700 bg-green-50 dark:bg-green-950 dark:text-green-300",
  red: "border-red-500/30 text-red-700 bg-red-50 dark:bg-red-950 dark:text-red-300",
  blue: "border-blue-500/30 text-blue-700 bg-blue-50 dark:bg-blue-950 dark:text-blue-300",
  indigo: "border-indigo-500/30 text-indigo-700 bg-indigo-50 dark:bg-indigo-950 dark:text-indigo-300",
  gray: "border-gray-500/30 text-gray-700 bg-gray-50 dark:bg-gray-950 dark:text-gray-300",
};

// Convert color name to Tailwind class
export const getTagClassName = (color: string): string => {
  return COLOR_CLASS_MAP[color] || COLOR_CLASS_MAP.gray;
};

// Hook return type
interface UseTagConfigReturn {
  tagConfig: Record<string, TagConfig>;
  validTags: string[];
  loading: boolean;
  getTagDisplayConfig: (tag: string) => TagDisplayConfig;
}

/**
 * Hook to fetch and manage dataset tag configuration from the backend.
 * Provides consistent tag display across all components.
 */
export function useTagConfig(apiKey: ApiKeyOrGetter): UseTagConfigReturn {
  const [tagConfig, setTagConfig] = useState<Record<string, TagConfig>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const subscription = DatasetService.getValidDatasetTags(() => {}, apiKey).subscribe(
      (response: any) => {
        setTagConfig(response.tags || {});
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching valid tags:", error);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [apiKey]);

  const validTags = Object.keys(tagConfig);

  const getTagDisplayConfig = useCallback((tag: string): TagDisplayConfig => {
    const config = tagConfig[tag];
    if (config) {
      return {
        label: config.label,
        description: config.description,
        className: getTagClassName(config.color),
      };
    }
    return {
      label: tag,
      description: "",
      className: getTagClassName("gray"),
    };
  }, [tagConfig]);

  return {
    tagConfig,
    validTags,
    loading,
    getTagDisplayConfig,
  };
}
